import { useEffect, useState } from 'react';
import { isCapacitorNativeSync } from '../native/bridge';
import { markBootSplashDone } from './bootSplashSignal';
import { LO_BODY_PATH, LO_LINE_PATH } from './siegelPaths';
import styles from './BootSplash.module.css';

// Der Vorspann ist seit 7.2.0.05 reines HTML/CSS („Siegel", CD-CI-Code/
// 33_Wortmarke-Animation/Siegel/). Vorher lag hier ein 11-MB-Video im festen
// Hochformat 1080x1920, das auf Querformat entweder verzerrt (cover) oder mit
// unscharfen Raendern (contain) lief. Die Produktionsform `lo-siegel--viewport`
// rechnet in `1vmin` und kann deshalb weder beschneiden noch verzerren — sie
// passt von der Uhr bis zum Desktop.
//
// Das Stylesheet liegt global unter styles/lorath-siegel.css (Import in
// main.tsx, wie lorath-loaders.css): die `.lo-*`-Klassen muessen unveraendert
// bleiben, ein CSS-Modul wuerde sie hashen und die SVG-`use`-Verweise brechen.

const MIN_VISIBLE_MS = 1200;
// Die Animation erreicht ihren Ruhezustand bei ~2,9 s (siehe _SPEZIFIKATION.md
// §Zeitverlauf: Atemholen 0,00 · Zuendung 0,38 · Siegel setzt 0,96 · Gravur
// 1,18 · Politur 2,15 · Ruhe ab 2,9). Danach atmet nur noch der Halo — es gibt
// also nichts mehr zu warten.
const ANIMATION_MS = 2900;
// Notbremse ab Mount. Beim Video deckelte sie Autoplay-Blocker und 404er; die
// CSS-Animation kann nicht mehr scheitern, aber der Vorspann darf unter keinen
// Umstaenden haengen bleiben.
const HARD_MAX_VISIBLE_MS = 4000;
const FADE_MS = 300;
// Nur Web: der Vorspann laeuft einmal pro Browser-Session. Ein Reload auf
// app.lorath.eu soll nicht jedes Mal Zeit kosten. Nativ ist ein Cold-Start ein
// echter App-Start — dort laeuft er immer.
const SPLASH_SESSION_KEY = 'lorath:splash-shown';

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Web-only Session-Gate. Nativ IMMER false → dort aendert sich nichts. */
function splashAlreadyShownThisSession(): boolean {
  if (typeof window === 'undefined' || isCapacitorNativeSync()) return false;
  try {
    return window.sessionStorage.getItem(SPLASH_SESSION_KEY) === '1';
  } catch {
    // Safari Private Mode wirft beim sessionStorage-Zugriff → im Zweifel zeigen.
    return false;
  }
}

function markSplashShownThisSession(): void {
  if (typeof window === 'undefined' || isCapacitorNativeSync()) return;
  try {
    window.sessionStorage.setItem(SPLASH_SESSION_KEY, '1');
  } catch {
    // s.o. — best effort, das Gate ist reiner Komfort.
  }
}

export function BootSplash() {
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  // Genau einmal beim ersten Render entschieden: die Markierung, die wir gleich
  // selbst setzen, darf den laufenden Splash nicht abschiessen.
  const [sessionSkip] = useState(splashAlreadyShownThisSession);
  const [hideRequested, setHideRequested] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [mounted, setMounted] = useState(!sessionSkip);
  const [shownAt] = useState(() => Date.now());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event: MediaQueryListEvent) => {
      setReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  useEffect(() => {
    if (sessionSkip) return;
    markSplashShownThisSession();
  }, [sessionSkip]);

  // CRIT 3: die Tour darf erst NACH dem Vorspann starten. `mounted` ist false,
  // sobald der Vorspann durch ist — inklusive des Web-Session-Skip-Falls, in dem
  // er gar nicht erst erscheint (sessionSkip → mounted startet false). In beiden
  // Faellen signalisieren wir "fertig". Das Latch ist idempotent.
  useEffect(() => {
    if (!mounted) markBootSplashDone();
  }, [mounted]);

  useEffect(() => {
    if (sessionSkip) return undefined;

    // Ohne Bewegung steht der Endzustand sofort (das Stylesheet loest das selbst
    // ueber prefers-reduced-motion) → Mindestzeit reicht. Sonst laeuft der
    // Vorspann bis zum Ruhezustand; die Notbremse deckelt zusaetzlich ab Mount.
    const runtime = reducedMotion ? MIN_VISIBLE_MS : ANIMATION_MS;
    const timeout = window.setTimeout(() => setHideRequested(true), runtime);
    const hardStop = window.setTimeout(() => setHideRequested(true), HARD_MAX_VISIBLE_MS);

    return () => {
      window.clearTimeout(timeout);
      window.clearTimeout(hardStop);
    };
  }, [reducedMotion, sessionSkip]);

  // Skip per Tastendruck. Das Overlay ist aria-hidden und darf deshalb keinen
  // Fokus bekommen — daher ein document-weiter Listener statt tabIndex.
  useEffect(() => {
    if (!mounted || exiting) return undefined;

    const handleKeyDown = () => setHideRequested(true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mounted, exiting]);

  useEffect(() => {
    if (!hideRequested) return undefined;

    let unmountTimer: number | undefined;
    const remainingMinimum = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt));
    const exitTimer = window.setTimeout(() => {
      setExiting(true);
      unmountTimer = window.setTimeout(() => {
        setMounted(false);
      }, FADE_MS);
    }, remainingMinimum);

    return () => {
      window.clearTimeout(exitTimer);
      if (unmountTimer !== undefined) window.clearTimeout(unmountTimer);
    };
  }, [hideRequested, shownAt]);

  if (!mounted) return null;

  return (
    <div
      className={`${styles.overlay} ${exiting ? styles.exiting : ''}`}
      aria-hidden="true"
      onPointerDown={() => setHideRequested(true)}
    >
      {/* Produktionsform: --u = 1vmin, position:fixed. Der Grund fuellt den
          ganzen Viewport, der Schriftzug skaliert gegen die KUERZERE Seite —
          damit gibt es weder Zuschnitt noch Verzerrung, egal ob Uhr, Handy
          hoch/quer, Tablet oder Desktop. */}
      <div className="lo-siegel lo-siegel--viewport" aria-hidden="true">
        {/* Der defs-Block liegt bewusst INNERHALB der Buehne: nur so erben die
            SVG-Verlaufsstufen die --lo-*-Token und der Vorspann bleibt
            umthembar. Die Literale hinter den var()-Kommas sind identische
            Rueckfallwerte. */}
        <svg className="lo-defs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <defs>
            {/* Koerper: senkrechter Verlauf, damit das Gold als Relief liest. */}
            <linearGradient id="lo-body-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" style={{ stopColor: 'var(--lo-gold-kante, #c99a5c)' }} />
              <stop offset=".38" style={{ stopColor: 'var(--lo-gold-tief, #a87c44)' }} />
              <stop offset="1" style={{ stopColor: 'var(--lo-gold-schatten, #6d4f28)' }} />
            </linearGradient>

            {/* Linienzeichnung: oben fast weiss, unten Markengold. */}
            <linearGradient id="lo-line-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" style={{ stopColor: 'var(--lo-gold-weiss, #fff6de)' }} />
              <stop offset=".45" style={{ stopColor: 'var(--lo-gold-hell, #f6dca6)' }} />
              <stop offset="1" style={{ stopColor: 'var(--lo-gold, #d9ab72)' }} />
            </linearGradient>

            {/* Spitzlicht-Band: nur in der Mitte hell, an beiden Kanten aus. */}
            <linearGradient id="lo-glanz-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" style={{ stopColor: 'var(--lo-gold-weiss, #fff6de)' }} stopOpacity="0" />
              <stop offset=".34" style={{ stopColor: 'var(--lo-gold-weiss, #fff6de)' }} stopOpacity=".12" />
              <stop offset=".5" style={{ stopColor: 'var(--lo-gold-weiss, #fff6de)' }} stopOpacity=".95" />
              <stop offset=".66" style={{ stopColor: 'var(--lo-gold-weiss, #fff6de)' }} stopOpacity=".12" />
              <stop offset="1" style={{ stopColor: 'var(--lo-gold-weiss, #fff6de)' }} stopOpacity="0" />
            </linearGradient>

            {/* Begrenzt das Spitzlicht auf die Buchstabenkoerper. Ohne diese
                Begrenzung wirkt es wie ein Reflex auf Glas statt auf Metall. */}
            <clipPath id="lo-glanz-clip" clipPathUnits="userSpaceOnUse">
              <use href="#lo-body" />
            </clipPath>

            <path id="lo-body" d={LO_BODY_PATH} />
            <path id="lo-line" d={LO_LINE_PATH} />
          </defs>
        </svg>

        <div className="lo-grund" />
        <div className="lo-kern" />
        <div className="lo-welle--a" />
        <div className="lo-welle--b" />
        <div className="lo-funken" />
        <div className="lo-halo" />
        <div className="lo-bett" />

        {/* Die Wortmarke: eine Gruppe, vier deckungsgleiche Ebenen. Jede bewegte
            Ebene ist ein eigenes <svg> und damit eine CSS-Box — SVG-Kinder
            bekommen in Blink und WebKit keine eigene Zeichenebene, sonst wuerde
            pro Frame die ganze Zeichnung neu gerastert. */}
        <div className="lo-mark">
          <svg
            className="lo-koerper-ebene"
            viewBox="0 0 1000 468"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <use className="lo-schatten" href="#lo-body" y="6" />
            <use className="lo-koerper" href="#lo-body" />
          </svg>
          <svg
            className="lo-gravur"
            viewBox="0 0 1000 468"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <use href="#lo-line" />
          </svg>
          <svg
            className="lo-gravur-heiss"
            viewBox="0 0 1000 468"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <use href="#lo-line" />
          </svg>
          <svg
            className="lo-glanz-ebene"
            viewBox="0 0 1000 468"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <g clipPath="url(#lo-glanz-clip)">
              <g className="lo-glanz">
                <rect className="lo-glanz-band" x="-150" y="-120" width="300" height="720" transform="skewX(-14)" />
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
}
